import React from 'react';
import axios from 'axios';

import { useAppSelector } from '@/store';
import { getErrorMessage } from '@/utils/errors';

import { ConfiguratorConfigRoot } from '../types';

const apiUrl = window.config.publicApiUrl;

export function useConfigFetch() {
  // Owner access token when signed in: the API enriches the config with owner-scoped data. Anonymous
  // configurator sessions (no token) still get the public config. Raw token, no Bearer, per the other calls.
  const authToken = useAppSelector((state) => state.authentication.access_token);
  const [configLoading, setConfigLoading] = React.useState<boolean>(true);
  const [configError, setConfigError] = React.useState<string | undefined>();
  const [configRoot, setConfigRoot] = React.useState<ConfiguratorConfigRoot>();

  React.useEffect(() => {
    let stale = false;
    setConfigError(undefined); // clear a prior (e.g. anonymous) error so a later success isn't masked
    // On a token DROP (sign-out), clear the previous owner's enriched config immediately so a slow or
    // failed anonymous re-fetch can never keep rendering it to the next visitor on a shared browser. Not
    // cleared on the anonymous->token UPGRADE, so that stays a seamless no-flash.
    if (!authToken) setConfigRoot(undefined);
    axios
      .get<ConfiguratorConfigRoot>(`${apiUrl}/configurator`, authToken ? { headers: { Authorization: authToken } } : undefined)
      .then((response) => {
        if (!stale) setConfigRoot(response.data);
      })
      .catch((error) => {
        // Log the message only, NOT the AxiosError: its config.headers.Authorization is the raw owner token.
        console.log(getErrorMessage(error));
        if (!stale) setConfigError('Could not load configuration. Please try again later.');
      })
      .finally(() => {
        if (!stale) setConfigLoading(false);
      });
    // Re-fetch when the token arrives (sign-in) so a session that started anonymous upgrades to the
    // enriched, owner-scoped config. The stale flag ignores an out-of-order response (e.g. the anonymous
    // fetch resolving after the token fetch), so the most recent request always wins. configLoading is
    // deliberately NOT reset to true on the upgrade, so the swap public->enriched is a seamless no-flash.
    return () => {
      stale = true;
    };
  }, [authToken]);

  return React.useMemo(
    () => ({
      configLoading,
      configError,
      configRoot,
    }),
    [configLoading, configError, configRoot],
  );
}
